import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ADMIN_ROLE, adminApi, type AdminPermissionList } from "api/adminApi";
import {
  Badge,
  Button,
  ErrorBanner,
  Field,
  Input,
  Modal,
  Select,
  TD,
  TEmpty,
  TH,
  THead,
  TRow,
  TSkeleton,
  Table,
} from "pages/admin/ui";

const FALLBACK_ERROR_MESSAGE = "잠시 후 다시 시도해 주세요";

type RoleDraft = { isNew: boolean; name: string; menus: string[] };
type AccountDraft = { isNew: boolean; email: string; role: string };

// 응답 처리 공통: 성공이면 목록을 다시 받고, 실패면 서버 메시지를 띄운다
type Result = { code: number; message?: string };

// 권한 관리: 역할(볼 수 있는 메뉴 묶음)을 정하고 계정에 역할을 지정한다. 고정 역할 admin 만 연다.
// admin 은 고정 역할이라 목록 맨 위에 두고 손대지 못한다. 내 계정도 바꾸거나 지울 수 없다 (서버도 거부).
export default function PermissionModal({
  myEmail,
  menus,
  onClose,
}: {
  myEmail: string;
  // 좌측 nav 메뉴 (key = 서버 메뉴 키)
  menus: { key: string; label: string }[];
  onClose: () => void;
}) {
  const [data, setData] = useState<AdminPermissionList>({
    roles: [],
    accounts: [],
  });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<string | null>(null);
  const [roleDraft, setRoleDraft] = useState<RoleDraft | null>(null);
  const [accountDraft, setAccountDraft] = useState<AccountDraft | null>(null);
  // 진행 중인 요청 하나만 둔다 (저장·삭제 중 다른 버튼을 막는다)
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await adminApi.permissionList();
      if (res.code === 0 && res.result) {
        setData(res.result);
        setMessage(null);
      } else {
        setMessage(res.message || FALLBACK_ERROR_MESSAGE);
      }
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // 요청 실행: 성공하면 onSuccess(입력 닫기) 후 목록을 다시 받는다
  const run = async (
    request: () => Promise<Result>,
    onSuccess?: () => void
  ) => {
    if (busy) return;
    setBusy(true);
    setMessage(null);
    try {
      const res = await request();
      if (res.code === 0) {
        onSuccess?.();
        await load();
      } else {
        setMessage(res.message || FALLBACK_ERROR_MESSAGE);
      }
    } catch (e) {
      setMessage(FALLBACK_ERROR_MESSAGE);
    } finally {
      setBusy(false);
    }
  };

  const menuLabels = (keys: string[]) =>
    menus
      .filter(({ key }) => keys.includes(key))
      .map(({ label }) => label)
      .join(" · ") || "-";

  const accountCount = (role: string) =>
    data.accounts.filter((a) => a.role === role).length;

  const saveRole = () =>
    roleDraft &&
    run(
      () => adminApi.roleSave(roleDraft.name.trim(), roleDraft.menus),
      () => setRoleDraft(null)
    );

  const deleteRole = (name: string) => {
    if (!window.confirm(`역할 "${name}" 을 지울까요?`)) return;
    run(() => adminApi.roleDelete(name));
  };

  const saveAccount = () =>
    accountDraft &&
    run(
      () => adminApi.accountSave(accountDraft.email.trim(), accountDraft.role),
      () => setAccountDraft(null)
    );

  const deleteAccount = (email: string) => {
    if (
      !window.confirm(
        `${email} 계정을 지울까요?\n지우면 어드민 콘솔에 들어올 수 없어요.`
      )
    ) {
      return;
    }
    run(() => adminApi.accountDelete(email));
  };

  const toggleRoleMenu = (key: string, checked: boolean) =>
    setRoleDraft((prev) =>
      prev
        ? {
            ...prev,
            menus: checked
              ? [...prev.menus, key]
              : prev.menus.filter((m) => m !== key),
          }
        : prev
    );

  return (
    <Modal
      title="권한 관리"
      width="760px"
      description="역할마다 볼 수 있는 메뉴를 정하고, 계정에 역할을 지정해요. admin 은 고정 역할로 모든 메뉴와 권한 관리를 쓸 수 있어요. 바꾼 권한은 바로 적용되고, 좌측 메뉴는 그 계정이 새로고침하면 바뀌어요."
      onClose={onClose}
    >
      {message && (
        <div className="mt-4">
          <ErrorBanner>{message}</ErrorBanner>
        </div>
      )}

      {/* ── 역할 ── */}
      <SectionTitle
        title="역할"
        action={
          <Button
            size="sm"
            disabled={!!roleDraft}
            onClick={() => setRoleDraft({ isNew: true, name: "", menus: [] })}
          >
            역할 추가
          </Button>
        }
      />
      {roleDraft && (
        <DraftPanel>
          <Field label="역할 이름">
            <Input
              value={roleDraft.name}
              placeholder="예: marketing"
              maxLength={30}
              disabled={!roleDraft.isNew}
              onChange={(e) =>
                setRoleDraft({ ...roleDraft, name: e.target.value })
              }
            />
          </Field>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {menus.map(({ key, label }) => (
              <label
                key={key}
                className="flex items-center gap-2 text-sm text-slate-700"
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[#0F55A7]"
                  checked={roleDraft.menus.includes(key)}
                  onChange={(e) => toggleRoleMenu(key, e.target.checked)}
                />
                {label}
              </label>
            ))}
          </div>
          <DraftActions
            onCancel={() => setRoleDraft(null)}
            onSave={saveRole}
            canSave={roleDraft.name.trim() !== "" && roleDraft.menus.length > 0}
            busy={busy}
          />
        </DraftPanel>
      )}
      <div className="mt-2">
        <Table>
          <THead>
            <TH>역할</TH>
            <TH>볼 수 있는 메뉴</TH>
            <TH>계정</TH>
            <TH />
          </THead>
          <tbody>
            {loading ? (
              <TSkeleton colSpan={4} rows={2} />
            ) : (
              <>
                <TRow>
                  <TD className="font-medium">
                    {ADMIN_ROLE}
                    <span className="ml-2">
                      <Badge tone="info">고정</Badge>
                    </span>
                  </TD>
                  <TD>모든 메뉴 + 권한 관리</TD>
                  <TD className="tabular-nums">{accountCount(ADMIN_ROLE)}</TD>
                  <TD />
                </TRow>
                {data.roles.map((role) => (
                  <TRow key={role.name}>
                    <TD className="font-medium">{role.name}</TD>
                    <TD>{menuLabels(role.menus)}</TD>
                    <TD className="tabular-nums">{accountCount(role.name)}</TD>
                    <TD>
                      <span className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!!roleDraft || busy}
                          onClick={() =>
                            setRoleDraft({
                              isNew: false,
                              name: role.name,
                              menus: role.menus,
                            })
                          }
                        >
                          수정
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={!!roleDraft || busy}
                          onClick={() => deleteRole(role.name)}
                        >
                          삭제
                        </Button>
                      </span>
                    </TD>
                  </TRow>
                ))}
              </>
            )}
          </tbody>
        </Table>
      </div>

      {/* ── 계정 ── */}
      <SectionTitle
        title="계정"
        action={
          <Button
            size="sm"
            disabled={!!accountDraft}
            onClick={() =>
              setAccountDraft({ isNew: true, email: "", role: "" })
            }
          >
            계정 추가
          </Button>
        }
      />
      {accountDraft && (
        <DraftPanel>
          <Field label="이메일">
            <Input
              value={accountDraft.email}
              placeholder="name@orwellhealth.org"
              disabled={!accountDraft.isNew}
              onChange={(e) =>
                setAccountDraft({ ...accountDraft, email: e.target.value })
              }
            />
          </Field>
          <Field label="역할">
            <Select
              value={accountDraft.role}
              onChange={(e) =>
                setAccountDraft({ ...accountDraft, role: e.target.value })
              }
            >
              <option value="" disabled>
                역할을 골라 주세요
              </option>
              <option value={ADMIN_ROLE}>{ADMIN_ROLE} (고정)</option>
              {data.roles.map((role) => (
                <option key={role.name} value={role.name}>
                  {role.name}
                </option>
              ))}
            </Select>
          </Field>
          <DraftActions
            onCancel={() => setAccountDraft(null)}
            onSave={saveAccount}
            canSave={
              accountDraft.email.trim() !== "" && accountDraft.role !== ""
            }
            busy={busy}
          />
        </DraftPanel>
      )}
      <div className="mt-2">
        <Table>
          <THead>
            <TH>이메일</TH>
            <TH>역할</TH>
            <TH />
          </THead>
          <tbody>
            {loading && <TSkeleton colSpan={3} />}
            {!loading && data.accounts.length === 0 && (
              <TEmpty colSpan={3}>
                {message ? "목록을 불러오지 못했어요" : "등록된 계정이 없어요"}
              </TEmpty>
            )}
            {data.accounts.map((account) => {
              const isMe = account.email === myEmail;
              return (
                <TRow key={account.email}>
                  <TD className="font-medium">
                    {account.email}
                    {isMe && (
                      <span className="ml-2">
                        <Badge tone="info">나</Badge>
                      </span>
                    )}
                  </TD>
                  <TD>{account.role}</TD>
                  <TD>
                    {/* 내 계정은 바꾸거나 지울 수 없다 */}
                    {!isMe && (
                      <span className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={!!accountDraft || busy}
                          onClick={() =>
                            setAccountDraft({
                              isNew: false,
                              email: account.email,
                              role: account.role,
                            })
                          }
                        >
                          수정
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={!!accountDraft || busy}
                          onClick={() => deleteAccount(account.email)}
                        >
                          삭제
                        </Button>
                      </span>
                    )}
                  </TD>
                </TRow>
              );
            })}
          </tbody>
        </Table>
      </div>
    </Modal>
  );
}

function SectionTitle({ title, action }: { title: string; action: ReactNode }) {
  return (
    <div className="mt-5 flex items-center justify-between">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      {action}
    </div>
  );
}

function DraftPanel({ children }: { children: ReactNode }) {
  return (
    <div className="mt-2 flex flex-col gap-3 rounded-md border border-slate-200 bg-slate-50 p-4">
      {children}
    </div>
  );
}

function DraftActions({
  onCancel,
  onSave,
  canSave,
  busy,
}: {
  onCancel: () => void;
  onSave: () => void;
  canSave: boolean;
  busy: boolean;
}) {
  return (
    <div className="flex justify-end gap-2">
      <Button disabled={busy} onClick={onCancel}>
        취소
      </Button>
      <Button
        variant="primary"
        disabled={!canSave}
        loading={busy}
        loadingLabel="저장 중…"
        onClick={onSave}
      >
        저장
      </Button>
    </div>
  );
}
